import type { ReactNode } from "react";
import { cn } from "../lib/cn";
import type { UseAddressAutocompleteReturn } from "../hooks/useAddressAutocomplete";

export interface AddressSuggestionListboxProps {
  /** The composing input's `useAddressAutocomplete` return value. */
  ac: UseAddressAutocompleteReturn;
  /** User-visible dropdown strings. They default to English; the enrollment form passes translations. */
  loadingText?: string | undefined;
  noMatchesText?: string | undefined;
  suggestionsLabel?: string | undefined;
}

/**
 * The Smarty suggestion dropdown (WAI-ARIA listbox) under an autocomplete-wired input.
 *
 * `AddressAutocompleteInput` and the M3P review step's own street inputs share
 * it. Render it inside a `relative` wrapper next to the input; it renders
 * nothing while the dropdown is closed.
 */
export function AddressSuggestionListbox({
  ac,
  loadingText = "Searching addresses…",
  noMatchesText = "No matching addresses",
  suggestionsLabel = "Address suggestions",
}: AddressSuggestionListboxProps): ReactNode {
  if (!ac.open) return null;
  return (
    <ul
      role="listbox"
      id={ac.listboxId}
      aria-label={suggestionsLabel}
      className="absolute left-0 right-0 top-full z-20 mt-1 max-h-60 overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
    >
      {ac.items.length > 0 ? (
        ac.items.map((item, i) => (
          <li
            key={ac.optionId(i)}
            id={ac.optionId(i)}
            role="option"
            aria-selected={i === ac.activeIndex}
            className={cn(
              "hover:bg-ice cursor-pointer px-3 py-2 text-sm text-slate-700",
              i === ac.activeIndex && "bg-ice",
            )}
            // preventDefault keeps focus in the input so blur doesn't close
            // the dropdown before the click's select fires.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => ac.select(i)}
          >
            {item.label}
          </li>
        ))
      ) : (
        <li className="px-3 py-2 text-sm text-slate-500">
          {ac.loading ? loadingText : noMatchesText}
        </li>
      )}
    </ul>
  );
}
